import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, FileText, Menu as MenuIcon, MapPin, Users,
  BarChart3, Settings, LogOut, TrendingUp, MessageCircle, ShoppingBag,
  Eye, Plus, Edit, Trash2, MousePointerClick, ExternalLink, X, Loader2, Tags
} from 'lucide-react';
import { navigation } from '../../mock/data';
import {
  adminSummary, adminEvents, adminResetAnalytics,
  fetchProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
  fetchArticles, adminCreateArticle, adminUpdateArticle, adminDeleteArticle,
  fetchBranches, adminCreateBranch, adminUpdateBranch, adminDeleteBranch,
  fetchSettings, adminUpdateSettings, adminUpdateAccount
} from '../../api/services';
import { apiErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/use-toast';
import LogoUpload from '../../components/LogoUpload';
import LocalImageUpload from '../../components/LocalImageUpload';
import PageBuilderAdmin from '../../components/page-builder/PageBuilderAdmin';
import { useSettings } from '../../context/SettingsContext';
import { DEFAULT_FOOTER, mergeFooter } from '../../lib/footerDefaults';
import { DEFAULT_PRODUCT_CATEGORIES, getProductCategories, slugifyCategoryId } from '../../lib/productCategories';

const menu = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'pages', label: 'Page Builder', icon: FileText },
  { id: 'menu', label: 'Menu Manager', icon: MenuIcon },
  { id: 'products', label: 'Produk', icon: Package },
  { id: 'categories', label: 'Kategori Produk', icon: Tags },
  { id: 'articles', label: 'Artikel', icon: FileText },
  { id: 'branches', label: 'Cabang & Agen', icon: MapPin },
  { id: 'settings', label: 'Settings', icon: Settings }
];

function Modal({ open, onClose, title, children, size = 'md' }) {
  if (!open) return null;
  const sizes = { md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-3xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${sizes[size]} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-lg text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <input {...props} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green focus:ring-2 focus:ring-japo-green/20 text-sm" />
    </div>
  );
}

function TextArea({ label, ...props }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <textarea {...props} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green focus:ring-2 focus:ring-japo-green/20 text-sm resize-none" />
    </div>
  );
}

export default function AdminDashboard() {
  const [active, setActive] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [settings, setSettings] = useState({});
  const [navItems, setNavItems] = useState([]);
  const [pageBuilder, setPageBuilder] = useState({});
  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState({ type: null, item: null });

  const navigate = useNavigate();
  const { toast } = useToast();

  const { refresh: refreshGlobalSettings } = useSettings();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, ev, prods, arts, brs, sett] = await Promise.all([
        adminSummary().catch(() => null),
        adminEvents(50).catch(() => []),
        fetchProducts().catch(() => []),
        fetchArticles({ limit: 100 }).catch(() => []),
        fetchBranches().catch(() => []),
        fetchSettings().catch(() => ({})),
      ]);
      setSummary(sum);
      setEvents(ev);
      setProducts(prods);
      setArticles(arts);
      setBranches(brs);
      setSettings(sett);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const n = Array.isArray(settings?.navigation) ? settings.navigation : navigation;
    setNavItems(n);
    setPageBuilder(settings?.page_builder || {});
  }, [settings]);

  useEffect(() => {
    const token = localStorage.getItem('belbel_token');
    const stored = localStorage.getItem('belbel_user');
    if (!token || !stored) {
      navigate('/admin/login');
      return;
    }
    setUser(JSON.parse(stored));
    loadData();
  }, [navigate, loadData]);

  const handleLogout = () => {
    localStorage.removeItem('belbel_token');
    localStorage.removeItem('belbel_user');
    toast({ title: 'Logout berhasil' });
    navigate('/admin/login');
  };

  const handleResetAnalytics = async () => {
    try {
      await adminResetAnalytics();
      toast({ title: 'Analytics direset' });
      loadData();
    } catch (err) {
      toast({ title: 'Gagal reset', description: apiErrorMessage(err), variant: 'destructive' });
    }
  };

  // Menu Manager (navigation)
  const openNavItem = (item = null, parentIndex = null, subIndex = null) => {
    setModal({
      type: 'nav',
      item: {
        draft: item || { label: '', path: '/', submenu: [] },
        parentIndex,
        subIndex,
      }
    });
  };

  const saveNavItem = async (e) => {
    e.preventDefault();
    const { draft, parentIndex, subIndex } = modal.item;
    const next = [...navItems];

    if (parentIndex === null) {
      // top-level
      if (subIndex === null) {
        // add new
        next.push({ ...draft, submenu: draft.submenu?.length ? draft.submenu : undefined });
      } else {
        next[subIndex] = { ...draft, submenu: draft.submenu?.length ? draft.submenu : undefined };
      }
    } else {
      // submenu item edit/add
      const parent = { ...(next[parentIndex] || {}) };
      const submenu = Array.isArray(parent.submenu) ? [...parent.submenu] : [];
      if (subIndex === null) submenu.push({ label: draft.label, path: draft.path });
      else submenu[subIndex] = { label: draft.label, path: draft.path };
      parent.submenu = submenu;
      next[parentIndex] = parent;
    }

    try {
      await adminUpdateSettings({ navigation: next });
      setNavItems(next);
      toast({ title: 'Menu tersimpan' });
      setModal({ type: null, item: null });
      await refreshGlobalSettings();
    } catch (err) {
      toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
    }
  };

  const deleteNavItem = async (parentIndex, subIndex = null) => {
    const next = [...navItems];
    if (subIndex === null) {
      next.splice(parentIndex, 1);
    } else {
      const parent = { ...(next[parentIndex] || {}) };
      const submenu = Array.isArray(parent.submenu) ? [...parent.submenu] : [];
      submenu.splice(subIndex, 1);
      parent.submenu = submenu.length ? submenu : undefined;
      next[parentIndex] = parent;
    }
    await adminUpdateSettings({ navigation: next });
    setNavItems(next);
    toast({ title: 'Menu dihapus' });
    await refreshGlobalSettings();
  };

  // Product CRUD
  const productCats = getProductCategories(settings);
  const openProduct = (p = null) => setModal({
    type: 'product',
    item: p || {
      name: '',
      category: productCats[0]?.id || 'cokelat',
      packaging: 'Powder Mix Gula (1 kg)',
      image: '',
      shopee: '#',
      tokopedia: '#',
      description: '',
      active: true,
      order: 0,
    },
  });
  const saveProduct = async (e) => {
    e.preventDefault();
    const p = modal.item;
    if (!p.image) {
      toast({ title: 'Gambar wajib', description: 'Upload gambar produk dari perangkat.', variant: 'destructive' });
      return;
    }
    if (!localStorage.getItem('belbel_token')) {
      toast({ title: 'Sesi habis', description: 'Silakan login ulang.', variant: 'destructive' });
      navigate('/admin/login');
      return;
    }
    try {
      if (p.id) await adminUpdateProduct(p.id, p);
      else await adminCreateProduct(p);
      toast({ title: 'Produk tersimpan' });
      setModal({ type: null, item: null });
      loadData();
    } catch (err) {
      const detail = apiErrorMessage(err);
      toast({ title: 'Gagal', description: detail, variant: 'destructive' });
      if (err?.response?.status === 401) navigate('/admin/login');
    }
  };
  const deleteProduct = async (p) => {
    if (!window.confirm(`Hapus produk "${p.name}"?`)) return;
    try {
      await adminDeleteProduct(p.id);
      toast({ title: 'Produk dihapus' });
      loadData();
    } catch (err) {
      toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
      if (err?.response?.status === 401) navigate('/admin/login');
    }
  };

  const saveProductCategories = async (cats) => {
    try {
      const latest = await fetchSettings().catch(() => settings);
      const nextBuilder = { ...(latest.page_builder || settings.page_builder || {}), product_categories: cats };
      await adminUpdateSettings({ page_builder: nextBuilder });
      setPageBuilder(nextBuilder);
      setSettings({ ...settings, ...latest, page_builder: nextBuilder });
      await refreshGlobalSettings();
      toast({ title: 'Kategori tersimpan' });
    } catch (err) {
      toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
      if (err?.response?.status === 401) navigate('/admin/login');
    }
  };

  // Article CRUD
  const openArticle = (a = null) => setModal({ type: 'article', item: a || { slug: '', title: '', excerpt: '', content: '', image: '', author: 'Tim BelBel', date: new Date().toLocaleDateString('id-ID'), category: 'Bisnis', published: true } });
  const saveArticle = async (e) => {
    e.preventDefault();
    const a = modal.item;
    try {
      if (a.id) await adminUpdateArticle(a.id, a);
      else await adminCreateArticle(a);
      toast({ title: 'Artikel tersimpan' });
      setModal({ type: null, item: null });
      loadData();
    } catch (err) {
      toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
    }
  };
  const deleteArticle = async (a) => {
    if (!window.confirm(`Hapus artikel "${a.title}"?`)) return;
    await adminDeleteArticle(a.id);
    toast({ title: 'Artikel dihapus' });
    loadData();
  };

  // Branch CRUD
  const openBranch = (b = null) => setModal({ type: 'branch', item: b || { title: '', region: '', address: '', map_link: '', whatsapp: '', image: '', order: 0 } });
  const saveBranch = async (e) => {
    e.preventDefault();
    const b = modal.item;
    try {
      if (b.id) await adminUpdateBranch(b.id, b);
      else await adminCreateBranch(b);
      toast({ title: 'Cabang tersimpan' });
      setModal({ type: null, item: null });
      loadData();
    } catch (err) {
      toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
    }
  };
  const deleteBranch = async (b) => {
    if (!window.confirm(`Hapus cabang "${b.title}"?`)) return;
    await adminDeleteBranch(b.id);
    toast({ title: 'Cabang dihapus' });
    loadData();
  };

  // Settings
  const saveSettings = async (e) => {
    if (e) e.preventDefault();
    try {
      await adminUpdateSettings(settings);
      await refreshGlobalSettings();
      toast({ title: 'Pengaturan tersimpan' });
    } catch (err) {
      toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
    }
  };

  const updateSettingField = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await adminUpdateSettings(patch);
      await refreshGlobalSettings();
      toast({ title: 'Logo tersimpan' });
    } catch (err) {
      toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
    }
  };

  // Account
  const [accountForm, setAccountForm] = useState({ email: '', username: '', current_password: '', new_password: '' });
  useEffect(() => {
    if (user) setAccountForm((s) => ({ ...s, email: user.email || '', username: user.username || '' }));
  }, [user]);
  const saveAccount = async (e) => {
    e.preventDefault();
    try {
      const updated = await adminUpdateAccount(accountForm);
      setUser(updated);
      localStorage.setItem('belbel_user', JSON.stringify(updated));
      toast({ title: 'Akun tersimpan' });
      setAccountForm((s) => ({ ...s, current_password: '', new_password: '' }));
    } catch (err) {
      toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className="w-64 bg-japo-green-dark text-white flex flex-col fixed inset-y-0 left-0 z-40">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-11 h-11 rounded-xl bg-japo-yellow flex items-center justify-center shadow-lg">
              <span className="text-japo-green-dark font-extrabold text-xl">B</span>
            </div>
            <div>
              <div className="font-extrabold">BelBel Admin</div>
              <div className="text-xs text-white/60">CMS Dashboard</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menu.map((m) => (
            <button
              key={m.id}
              onClick={() => setActive(m.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                active === m.id ? 'bg-japo-yellow text-japo-green-dark' : 'text-white/80 hover:bg-white/10'
              }`}
            >
              <m.icon className="w-4 h-4" />
              {m.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-japo-yellow text-japo-green-dark font-bold flex items-center justify-center">
              {(user.username || 'A')[0].toUpperCase()}
            </div>
            <div>
              <div className="font-bold text-sm">{user.username}</div>
              <div className="text-xs text-white/60">Administrator</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-red-500 text-white text-sm font-semibold transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">{menu.find((m) => m.id === active)?.label}</h1>
            <p className="text-gray-500 text-sm mt-1">Kelola konten website BelBel dari sini</p>
          </div>
          <Link to="/" target="_blank" className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-japo-green px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-700 shadow-sm">
            <ExternalLink className="w-4 h-4" /> Lihat Website
          </Link>
        </div>

        {loading && <div className="flex items-center justify-center py-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Memuat...</div>}

        {active === 'dashboard' && summary && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              {[
                { label: 'Total Visitors', value: summary.total_visits, icon: Users, color: 'bg-blue-500' },
                { label: 'Unique Visitors', value: summary.unique_visits, icon: Eye, color: 'bg-purple-500' },
                { label: 'WhatsApp Clicks', value: summary.whatsapp_clicks, icon: MessageCircle, color: 'bg-[#25D366]' },
                { label: 'Marketplace Clicks', value: summary.marketplace_clicks, icon: ShoppingBag, color: 'bg-japo-yellow' }
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-11 h-11 rounded-xl ${s.color} flex items-center justify-center text-white`}>
                      <s.icon className="w-5 h-5" />
                    </div>
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="text-3xl font-extrabold text-gray-900">{s.value}</div>
                  <div className="text-xs text-gray-500 font-semibold mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-lg text-gray-900 mb-4">Halaman Terpopuler</h3>
                {summary.top_pages.length === 0 ? (
                  <p className="text-gray-500 text-sm">Belum ada data.</p>
                ) : (
                  <div className="space-y-3">
                    {summary.top_pages.map((p) => {
                      const max = Math.max(...summary.top_pages.map((x) => x.count));
                      const pct = (p.count / max) * 100;
                      return (
                        <div key={p.page}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700">{p.page === '/' ? '/ (Home)' : p.page}</span>
                            <span className="font-bold text-japo-green">{p.count} visits</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-japo-green rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-bold text-lg text-gray-900 mb-4">Interaksi Terbanyak</h3>
                {summary.top_events.length === 0 ? (
                  <p className="text-gray-500 text-sm">Belum ada interaksi.</p>
                ) : (
                  <div className="space-y-3">
                    {summary.top_events.map((ev) => (
                      <div key={ev.type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-japo-yellow/20 flex items-center justify-center">
                            <MousePointerClick className="w-4 h-4 text-japo-yellow" />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{ev.type}</span>
                        </div>
                        <span className="font-bold text-japo-green">{ev.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {active === 'analytics' && (
          <div className="space-y-6">
            <div className="flex justify-between">
              <div className="text-sm text-gray-600">Menampilkan {events.length} interaksi terbaru</div>
              <button
                onClick={handleResetAnalytics}
                className="text-sm text-red-500 hover:text-red-700 font-semibold"
              >
                Reset Data
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 font-bold text-gray-900">Interaction Log (50 terakhir)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">Type</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">Meta</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">Page</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-t border-gray-50">
                        <td className="px-6 py-3">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${
                            e.type.includes('whatsapp') ? 'bg-green-100 text-green-700' :
                            e.type.includes('marketplace') ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>{e.type}</span>
                        </td>
                        <td className="px-6 py-3 text-gray-600 text-xs font-mono">{JSON.stringify(e.meta || {})}</td>
                        <td className="px-6 py-3 text-gray-500 text-xs">{e.page || '-'}</td>
                        <td className="px-6 py-3 text-gray-500 text-xs">{new Date(e.timestamp).toLocaleString('id-ID')}</td>
                      </tr>
                    ))}
                    {events.length === 0 && (
                      <tr><td colSpan={4} className="px-6 py-12 text-center text-gray-500">Belum ada interaksi.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {active === 'pages' && (
          <PageBuilderAdmin
            pageBuilder={pageBuilder}
            setPageBuilder={setPageBuilder}
            toast={toast}
            refreshGlobalSettings={refreshGlobalSettings}
          />
        )}

        {active === 'menu' && (
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-lg text-gray-900">Menu Manager (Header)</h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    if (!window.confirm('Kembalikan menu ke default?')) return;
                    try {
                      await adminUpdateSettings({ navigation });
                      setNavItems(navigation);
                      toast({ title: 'Menu direset ke default' });
                      await refreshGlobalSettings();
                    } catch (err) {
                      toast({ title: 'Gagal', description: apiErrorMessage(err), variant: 'destructive' });
                    }
                  }}
                  className="text-xs font-semibold text-red-600 hover:text-red-700"
                >
                  Reset ke Default
                </button>
                <button onClick={() => openNavItem()} className="inline-flex items-center gap-2 bg-japo-green hover:bg-japo-green-dark text-white text-sm font-semibold px-4 py-2 rounded-lg">
                  <Plus className="w-4 h-4" /> Tambah Menu
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {navItems.map((n, idx) => (
                <div key={n.label} className="border border-gray-100 rounded-xl">
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div className="cursor-move text-gray-400">⋮⋮</div>
                      <div>
                        <div className="font-bold text-gray-900">{n.label}</div>
                        <div className="text-xs text-gray-500">{n.path}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openNavItem(n, null, idx)} className="p-2 hover:bg-gray-100 rounded-lg"><Edit className="w-4 h-4 text-gray-600" /></button>
                      <button onClick={() => deleteNavItem(idx)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                  </div>
                  {n.submenu && (
                    <div className="px-4 pb-3 pl-12 space-y-1">
                      {n.submenu.map((s, sidx) => (
                        <div key={s.label} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 text-sm">
                          <div className="text-gray-700">↳ {s.label} <span className="text-gray-400 text-xs">({s.path})</span></div>
                          <div className="flex gap-1">
                            <button onClick={() => openNavItem(s, idx, sidx)} className="p-1.5 hover:bg-gray-100 rounded-lg"><Edit className="w-4 h-4 text-gray-600" /></button>
                            <button onClick={() => deleteNavItem(idx, sidx)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                          </div>
                        </div>
                      ))}
                      <button onClick={() => openNavItem({ label: '', path: '/' }, idx, null)} className="text-xs font-semibold text-japo-green hover:underline">
                        + Tambah Submenu
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <Modal
          open={modal.type === 'nav'}
          onClose={() => setModal({ type: null, item: null })}
          title={modal.item?.parentIndex === null ? 'Menu Item' : 'Submenu Item'}
        >
          <form onSubmit={saveNavItem} className="space-y-4">
            <Field
              label="Label"
              value={modal.item?.draft?.label || ''}
              onChange={(e) => setModal((m) => ({ ...m, item: { ...m.item, draft: { ...m.item.draft, label: e.target.value } } }))}
              required
            />
            <Field
              label="Path/URL"
              value={modal.item?.draft?.path || ''}
              onChange={(e) => setModal((m) => ({ ...m, item: { ...m.item, draft: { ...m.item.draft, path: e.target.value } } }))}
              required
            />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setModal({ type: null, item: null })} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold">
                Batal
              </button>
              <button type="submit" className="px-4 py-2 rounded-lg bg-japo-green text-white text-sm font-semibold">
                Simpan
              </button>
            </div>
          </form>
        </Modal>

        {active === 'products' && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <h3 className="font-bold text-lg text-gray-900">Daftar Produk ({products.length})</h3>
              <button onClick={() => openProduct()} className="inline-flex items-center gap-2 bg-japo-green hover:bg-japo-green-dark text-white text-sm font-semibold px-4 py-2 rounded-lg">
                <Plus className="w-4 h-4" /> Tambah Produk
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 font-semibold text-gray-600">Produk</th>
                    <th className="text-left px-6 py-3 font-semibold text-gray-600">Kategori</th>
                    <th className="text-left px-6 py-3 font-semibold text-gray-600">Kemasan</th>
                    <th className="text-right px-6 py-3 font-semibold text-gray-600">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-gray-50 hover:bg-gray-50">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <img src={p.image} alt={p.name} className="w-10 h-10 rounded-lg object-cover" />
                          <span className="font-semibold text-gray-900">{p.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-3 capitalize text-gray-600">{p.category}</td>
                      <td className="px-6 py-3 text-gray-600">{p.packaging}</td>
                      <td className="px-6 py-3 text-right">
                        <button onClick={() => openProduct(p)} className="p-2 hover:bg-gray-100 rounded-lg inline-flex"><Edit className="w-4 h-4 text-gray-600" /></button>
                        <button onClick={() => deleteProduct(p)} className="p-2 hover:bg-red-50 rounded-lg inline-flex"><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {active === 'categories' && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-lg text-gray-900">Kategori Produk</h3>
                <p className="text-sm text-gray-500 mt-1">Kelola kategori filter di halaman Produk & form produk.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const label = window.prompt('Nama kategori baru:');
                  if (!label || !label.trim()) return;
                  const id = slugifyCategoryId(label);
                  const cats = [...productCats];
                  if (cats.some((c) => c.id === id)) {
                    toast({ title: 'ID kategori sudah ada', variant: 'destructive' });
                    return;
                  }
                  saveProductCategories([...cats, { id, label: label.trim() }]);
                }}
                className="inline-flex items-center gap-2 bg-japo-green hover:bg-japo-green-dark text-white text-sm font-semibold px-4 py-2 rounded-lg"
              >
                <Plus className="w-4 h-4" /> Tambah Kategori
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {(productCats.length ? productCats : DEFAULT_PRODUCT_CATEGORIES).map((c, idx) => (
                <div key={c.id} className="p-4 flex items-center gap-3 hover:bg-gray-50">
                  <div className="flex-1 grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Label</label>
                      <input
                        value={c.label}
                        onChange={(e) => {
                          const next = productCats.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x));
                          setSettings({
                            ...settings,
                            page_builder: { ...(settings.page_builder || {}), product_categories: next },
                          });
                        }}
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">ID (untuk filter URL)</label>
                      <input
                        value={c.id}
                        onChange={(e) => {
                          const next = productCats.map((x, i) => (i === idx ? { ...x, id: slugifyCategoryId(e.target.value) } : x));
                          setSettings({
                            ...settings,
                            page_builder: { ...(settings.page_builder || {}), product_categories: next },
                          });
                        }}
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.confirm(`Hapus kategori "${c.label}"?`)) return;
                      saveProductCategories(productCats.filter((_, i) => i !== idx));
                    }}
                    className="p-2 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-gray-100">
              <button
                type="button"
                onClick={() => saveProductCategories(getProductCategories(settings))}
                className="bg-japo-green hover:bg-japo-green-dark text-white font-bold py-3 px-6 rounded-lg"
              >
                Simpan Kategori
              </button>
            </div>
          </div>
        )}

        {active === 'articles' && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <h3 className="font-bold text-lg text-gray-900">Artikel ({articles.length})</h3>
              <button onClick={() => openArticle()} className="inline-flex items-center gap-2 bg-japo-green hover:bg-japo-green-dark text-white text-sm font-semibold px-4 py-2 rounded-lg">
                <Plus className="w-4 h-4" /> Tulis Artikel
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {articles.map((a) => (
                <div key={a.id} className="p-6 flex items-center gap-4 hover:bg-gray-50">
                  <img src={a.image} alt={a.title} className="w-20 h-14 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="font-bold text-gray-900">{a.title}</div>
                    <div className="text-xs text-gray-500 mt-1">{a.category} · {a.date} · {a.author}</div>
                  </div>
                  <button onClick={() => openArticle(a)} className="p-2 hover:bg-gray-100 rounded-lg"><Edit className="w-4 h-4 text-gray-600" /></button>
                  <button onClick={() => deleteArticle(a)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {active === 'branches' && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <h3 className="font-bold text-lg text-gray-900">Cabang & Agen ({branches.length})</h3>
              <button onClick={() => openBranch()} className="inline-flex items-center gap-2 bg-japo-green hover:bg-japo-green-dark text-white text-sm font-semibold px-4 py-2 rounded-lg">
                <Plus className="w-4 h-4" /> Tambah Cabang
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {branches.map((b) => (
                <div key={b.id} className="p-6 flex items-center gap-4 hover:bg-gray-50">
                  <img src={b.image} alt={b.title} className="w-20 h-16 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="font-bold text-gray-900">{b.title}</div>
                    <div className="text-xs text-gray-500 mt-1 line-clamp-1">{b.address}</div>
                  </div>
                  <button onClick={() => openBranch(b)} className="p-2 hover:bg-gray-100 rounded-lg"><Edit className="w-4 h-4 text-gray-600" /></button>
                  <button onClick={() => deleteBranch(b)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {active === 'settings' && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 lg:col-span-2">
              <h3 className="font-bold text-lg text-gray-900 mb-1">Logo & Media</h3>
              <p className="text-sm text-gray-500 mb-5">Upload logo untuk header, footer, dan favicon. Perubahan langsung tersimpan.</p>
              <div className="grid md:grid-cols-3 gap-5">
                <LogoUpload
                  label="Logo Header"
                  value={settings.logo_header || ''}
                  onChange={(v) => updateSettingField({ logo_header: v })}
                  maxDim={400}
                />
                <LogoUpload
                  label="Logo Footer"
                  value={settings.logo_footer || ''}
                  onChange={(v) => updateSettingField({ logo_footer: v })}
                  maxDim={400}
                />
                <LogoUpload
                  label="Favicon (32-128px, square)"
                  value={settings.favicon || ''}
                  onChange={(v) => updateSettingField({ favicon: v })}
                  maxDim={128}
                  aspect="square"
                />
              </div>
            </div>

            <form onSubmit={saveAccount} className="bg-white rounded-2xl p-6 border border-gray-100">
              <h3 className="font-bold text-lg text-gray-900 mb-4">Akun Admin</h3>
              <div className="space-y-4">
                <Field label="Email" type="email" value={accountForm.email} onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })} />
                <Field label="Username" type="text" value={accountForm.username} onChange={(e) => setAccountForm({ ...accountForm, username: e.target.value })} />
                <Field label="Password Lama (untuk ubah password)" type="password" value={accountForm.current_password} onChange={(e) => setAccountForm({ ...accountForm, current_password: e.target.value })} />
                <Field label="Password Baru" type="password" value={accountForm.new_password} onChange={(e) => setAccountForm({ ...accountForm, new_password: e.target.value })} />
                <button type="submit" className="w-full bg-japo-green hover:bg-japo-green-dark text-white font-bold py-3 rounded-lg">Simpan Akun</button>
              </div>
            </form>

            <form onSubmit={saveSettings} className="bg-white rounded-2xl p-6 border border-gray-100">
              <h3 className="font-bold text-lg text-gray-900 mb-4">Branding & Kontak</h3>
              <div className="space-y-3">
                <Field label="Nama Brand" value={settings.brand_name || ''} onChange={(e) => setSettings({ ...settings, brand_name: e.target.value })} />
                <Field label="Nama Lengkap Brand" value={settings.brand_full || ''} onChange={(e) => setSettings({ ...settings, brand_full: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="WhatsApp" value={settings.whatsapp_number || ''} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} />
                  <Field label="WhatsApp Alt" value={settings.whatsapp_number_alt || ''} onChange={(e) => setSettings({ ...settings, whatsapp_number_alt: e.target.value })} />
                </div>
                <Field label="Email" type="email" value={settings.email || ''} onChange={(e) => setSettings({ ...settings, email: e.target.value })} />
                <Field label="Alamat" value={settings.address || ''} onChange={(e) => setSettings({ ...settings, address: e.target.value })} />
                <button type="submit" className="w-full bg-japo-green hover:bg-japo-green-dark text-white font-bold py-3 rounded-lg">Simpan Pengaturan</button>
              </div>
            </form>

            <form onSubmit={saveSettings} className="bg-white rounded-2xl p-6 border border-gray-100 lg:col-span-2">
              <h3 className="font-bold text-lg text-gray-900 mb-4">SEO Settings</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Meta Title" value={settings.seo_title || ''} onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })} />
                <Field label="Meta Keywords" value={settings.seo_keywords || ''} onChange={(e) => setSettings({ ...settings, seo_keywords: e.target.value })} />
                <div className="md:col-span-2">
                  <TextArea label="Meta Description" rows={3} value={settings.seo_description || ''} onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })} />
                </div>
              </div>
              <button type="submit" className="mt-4 bg-japo-green hover:bg-japo-green-dark text-white font-bold py-3 px-6 rounded-lg">Simpan SEO</button>
            </form>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const latest = await fetchSettings().catch(() => settings);
                  const footer = mergeFooter(settings?.page_builder?.footer);
                  const nextBuilder = { ...(latest.page_builder || settings.page_builder || {}), footer };
                  await adminUpdateSettings({ page_builder: nextBuilder });
                  setPageBuilder(nextBuilder);
                  setSettings({ ...settings, ...latest, page_builder: nextBuilder });
                  await refreshGlobalSettings();
                  toast({ title: 'Footer tersimpan' });
                } catch (err) {
                  const detail = apiErrorMessage(err);
                  toast({ title: 'Gagal', description: detail, variant: 'destructive' });
                  if (err?.response?.status === 401) {
                    navigate('/admin/login');
                  }
                }
              }}
              className="bg-white rounded-2xl p-6 border border-gray-100 lg:col-span-2"
            >
              <h3 className="font-bold text-lg text-gray-900 mb-1">Edit Footer</h3>
              <p className="text-sm text-gray-500 mb-5">Data default sudah terisi — tinggal edit lalu simpan.</p>
              {(() => {
                const footerForm = mergeFooter(settings?.page_builder?.footer);
                const setFooterField = (key, value) => setSettings({
                  ...settings,
                  page_builder: {
                    ...(settings.page_builder || {}),
                    footer: { ...footerForm, [key]: value },
                  },
                });
                return (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <TextArea
                    label="Teks Tentang (kolom kiri)"
                    rows={3}
                    value={footerForm.about}
                    onChange={(e) => setFooterField('about', e.target.value)}
                    placeholder={DEFAULT_FOOTER.about}
                  />
                </div>
                <Field
                  label="Judul Quick Links"
                  value={footerForm.quick_title}
                  onChange={(e) => setFooterField('quick_title', e.target.value)}
                  placeholder={DEFAULT_FOOTER.quick_title}
                />
                <Field
                  label="Judul Kategori Produk"
                  value={footerForm.categories_title}
                  onChange={(e) => setFooterField('categories_title', e.target.value)}
                  placeholder={DEFAULT_FOOTER.categories_title}
                />
                <Field
                  label="Judul Hubungi Kami"
                  value={footerForm.contact_title}
                  onChange={(e) => setFooterField('contact_title', e.target.value)}
                  placeholder={DEFAULT_FOOTER.contact_title}
                />
                <Field
                  label="Teks Copyright"
                  value={footerForm.copyright}
                  onChange={(e) => setFooterField('copyright', e.target.value)}
                  placeholder={DEFAULT_FOOTER.copyright}
                />
                <Field
                  label="Link Facebook (opsional)"
                  value={footerForm.facebook}
                  onChange={(e) => setFooterField('facebook', e.target.value)}
                  placeholder="https://facebook.com/..."
                />
                <Field
                  label="Link Youtube (opsional)"
                  value={footerForm.youtube}
                  onChange={(e) => setFooterField('youtube', e.target.value)}
                  placeholder="https://youtube.com/..."
                />
              </div>
                );
              })()}
              <p className="text-xs text-gray-500 mt-3">Alamat, WA, email, Instagram diambil dari Branding & Kontak. Menu Quick Links mengikuti Menu Manager.</p>
              <button type="submit" className="mt-4 bg-japo-green hover:bg-japo-green-dark text-white font-bold py-3 px-6 rounded-lg">Simpan Footer</button>
            </form>
          </div>
        )}
      </main>

      {/* Product Modal */}
      <Modal open={modal.type === 'product'} onClose={() => setModal({ type: null, item: null })} title={modal.item?.id ? 'Edit Produk' : 'Tambah Produk'} size="lg">
        {modal.item && (
          <form onSubmit={saveProduct} className="space-y-3">
            <Field label="Nama Produk *" required value={modal.item.name} onChange={(e) => setModal({ ...modal, item: { ...modal.item, name: e.target.value } })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Kategori *</label>
                <select required value={modal.item.category} onChange={(e) => setModal({ ...modal, item: { ...modal.item, category: e.target.value } })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green text-sm">
                  {productCats.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <Field label="Kemasan" value={modal.item.packaging} onChange={(e) => setModal({ ...modal, item: { ...modal.item, packaging: e.target.value } })} />
            </div>
            <LocalImageUpload
              label="Gambar Produk"
              required
              value={modal.item.image || ''}
              onChange={(v) => setModal({ ...modal, item: { ...modal.item, image: v } })}
              maxDim={1000}
            />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Link Shopee" value={modal.item.shopee} onChange={(e) => setModal({ ...modal, item: { ...modal.item, shopee: e.target.value } })} placeholder="https://s.shopee.co.id/..." />
              <Field label="Link TikTok Shop" value={modal.item.tokopedia} onChange={(e) => setModal({ ...modal, item: { ...modal.item, tokopedia: e.target.value } })} placeholder="https://vt.tiktok.com/..." />
            </div>
            <TextArea label="Deskripsi" rows={3} value={modal.item.description} onChange={(e) => setModal({ ...modal, item: { ...modal.item, description: e.target.value } })} />
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setModal({ type: null, item: null })} className="flex-1 py-2.5 border border-gray-200 rounded-lg font-semibold text-gray-700 hover:bg-gray-50">Batal</button>
              <button type="submit" className="flex-1 py-2.5 bg-japo-green hover:bg-japo-green-dark text-white rounded-lg font-bold">Simpan</button>
            </div>
          </form>
        )}
      </Modal>

      {/* Article Modal */}
      <Modal open={modal.type === 'article'} onClose={() => setModal({ type: null, item: null })} title={modal.item?.id ? 'Edit Artikel' : 'Tulis Artikel'} size="xl">
        {modal.item && (
          <form onSubmit={saveArticle} className="space-y-3">
            <Field label="Judul *" required value={modal.item.title} onChange={(e) => setModal({ ...modal, item: { ...modal.item, title: e.target.value } })} />
            <Field label="Slug (URL) *" required value={modal.item.slug} onChange={(e) => setModal({ ...modal, item: { ...modal.item, slug: e.target.value } })} />
            <div className="grid grid-cols-3 gap-3">
              <Field label="Kategori" value={modal.item.category} onChange={(e) => setModal({ ...modal, item: { ...modal.item, category: e.target.value } })} />
              <Field label="Penulis" value={modal.item.author} onChange={(e) => setModal({ ...modal, item: { ...modal.item, author: e.target.value } })} />
              <Field label="Tanggal" value={modal.item.date} onChange={(e) => setModal({ ...modal, item: { ...modal.item, date: e.target.value } })} />
            </div>
            <LocalImageUpload
              label="Gambar Artikel"
              required
              value={modal.item.image || ''}
              onChange={(v) => setModal({ ...modal, item: { ...modal.item, image: v } })}
              maxDim={1200}
            />
            <TextArea label="Ringkasan *" required rows={2} value={modal.item.excerpt} onChange={(e) => setModal({ ...modal, item: { ...modal.item, excerpt: e.target.value } })} />
            <TextArea label="Konten Lengkap" rows={8} value={modal.item.content} onChange={(e) => setModal({ ...modal, item: { ...modal.item, content: e.target.value } })} />
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setModal({ type: null, item: null })} className="flex-1 py-2.5 border border-gray-200 rounded-lg font-semibold text-gray-700 hover:bg-gray-50">Batal</button>
              <button type="submit" className="flex-1 py-2.5 bg-japo-green hover:bg-japo-green-dark text-white rounded-lg font-bold">Simpan</button>
            </div>
          </form>
        )}
      </Modal>

      {/* Branch Modal */}
      <Modal open={modal.type === 'branch'} onClose={() => setModal({ type: null, item: null })} title={modal.item?.id ? 'Edit Cabang' : 'Tambah Cabang'} size="lg">
        {modal.item && (
          <form onSubmit={saveBranch} className="space-y-3">
            <Field label="Nama Cabang *" required value={modal.item.title} onChange={(e) => setModal({ ...modal, item: { ...modal.item, title: e.target.value } })} />
            <Field label="Region/Wilayah *" required value={modal.item.region} onChange={(e) => setModal({ ...modal, item: { ...modal.item, region: e.target.value } })} />
            <TextArea label="Alamat Lengkap *" required rows={3} value={modal.item.address} onChange={(e) => setModal({ ...modal, item: { ...modal.item, address: e.target.value } })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="WhatsApp *" required placeholder="6281xxxx" value={modal.item.whatsapp} onChange={(e) => setModal({ ...modal, item: { ...modal.item, whatsapp: e.target.value } })} />
              <Field label="Google Maps Link" value={modal.item.map_link} onChange={(e) => setModal({ ...modal, item: { ...modal.item, map_link: e.target.value } })} />
            </div>
            <LocalImageUpload
              label="Gambar Cabang"
              required
              value={modal.item.image || ''}
              onChange={(v) => setModal({ ...modal, item: { ...modal.item, image: v } })}
              maxDim={1200}
            />
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setModal({ type: null, item: null })} className="flex-1 py-2.5 border border-gray-200 rounded-lg font-semibold text-gray-700 hover:bg-gray-50">Batal</button>
              <button type="submit" className="flex-1 py-2.5 bg-japo-green hover:bg-japo-green-dark text-white rounded-lg font-bold">Simpan</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
